import { SignIn, SignUp } from "@clerk/react";
import { Blade, Jets, FlightPath, Reveal, RevealWords, SpinLogo } from "@/components/AuthHeroFX";

const clerkAppearance = {
  elements: {
    rootBox: "w-full flex justify-center",
    cardBox: "w-full max-w-[400px] shadow-[0_18px_40px_-12px_rgba(15,29,61,0.28)] rounded-2xl lg:rounded-md",
    logoBox: "hidden",
  },
};

const HEADLINE = "Your next trip takes off here.";
const CABIN_OVERLAY = "bg-[linear-gradient(180deg,rgba(10,16,58,0.92)_0%,rgba(14,24,82,0.78)_55%,rgba(14,24,82,0.9)_100%)]";
// Active sign-in effects; ?fx=cabin|blade|logo|jets|path previews one on its own
const ACTIVE = ["cabin", "logo", "blade"];
const FX = new URLSearchParams(window.location.search).get("fx");
const on = (k) => (FX ? FX === k : ACTIVE.includes(k));
const still = !on("logo");

function HeroLayers() {
  return null;
}

const Sky = ({ gutter }) => (on("jets") ? <div className={`relative flex-1 my-2 ${gutter}`}><Jets className="inset-0 h-full" /></div> : null);

function HeroTop() {
  return (
    <>
      {on("blade") && <Blade />}
      {on("path") && <FlightPath />}
    </>
  );
}

function MobileHero() {
  return (
    <header className="on-navy lg:hidden relative h-[46vh] min-h-[360px] overflow-hidden" data-testid="auth-mobile-hero">
      <img src="/cabin-bw.jpg" alt="Private jet cabin" className={`${on("cabin") ? "cabin-drift-m" : ""} absolute inset-0 w-full h-full object-cover object-center`} />
      <HeroLayers />
      <div className={`absolute inset-0 ${CABIN_OVERLAY}`} />
      <HeroTop />
      <div className="relative h-full flex flex-col justify-between px-7 pt-8 pb-24">
        <SpinLogo className="h-28" testId="auth-mobile-wordmark" still={still} />
        <Sky gutter="-mx-7" />
        <div>
          <Reveal delay={0.35} still={still}><p className="eyebrow mb-3 !text-[#C6A55C] !font-bold" data-testid="auth-mobile-eyebrow">JSG Portal: Broker &amp; Flight Operations</p></Reveal>
          <RevealWords text={HEADLINE} start={0.5} still={still} className="font-serif-display text-[2rem] sm:text-4xl text-white leading-[1.12] uppercase" />
        </div>
      </div>
    </header>
  );
}

function DesktopHero() {
  return (
    <aside className="on-navy hidden lg:flex relative w-[52%] min-h-screen overflow-hidden" data-testid="auth-desktop-hero">
      <img src="/cabin-bw.jpg" alt="Private jet cabin" className={`${on("cabin") ? "cabin-drift" : "scale-[1.35] origin-[12%_45%]"} absolute inset-0 w-full h-full object-cover object-[22%_center]`} data-testid="auth-desktop-cabin-img" />
      <HeroLayers />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(10,16,70,0.86)_0%,rgba(22,32,125,0.66)_45%,rgba(12,18,80,0.9)_100%)]" />
      <HeroTop />
      <div className="relative flex flex-col justify-between px-14 py-14 w-full">
        <SpinLogo className="h-40" testId="auth-desktop-wordmark" still={still} />
        <Sky gutter="-mx-14" />
        <div className="max-w-2xl">
          <Reveal delay={0.45} still={still}><p className="eyebrow mb-6 !text-[0.8rem] !tracking-[0.3em] !text-[#C6A55C] !font-bold" data-testid="auth-desktop-eyebrow">JSG Portal: Broker &amp; Flight Operations</p></Reveal>
          <RevealWords text={HEADLINE} start={0.6} still={still} className="font-serif-display text-5xl xl:text-6xl 2xl:text-7xl text-white leading-[1.1] uppercase" />
          <Reveal delay={1.4} still={still}>
            <p className="mt-8 text-zinc-200 text-base xl:text-lg leading-relaxed max-w-xl" data-testid="auth-desktop-blurb">
              Request quotes, compare options from certified charter operators, and deliver branded proposals to your clients, all in one private workspace.
            </p>
          </Reveal>
        </div>
      </div>
    </aside>
  );
}

export default function AuthPage({ mode }) {
  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#090A0D]" data-testid={mode === "in" ? "sign-in-page" : "sign-up-page"}>
      <MobileHero />
      <DesktopHero />
      <main className="relative z-10 flex-1 flex flex-col items-center lg:justify-center px-5 -mt-20 lg:mt-0 pb-12 lg:py-12 gap-7">
        <Reveal delay={0.9} still={still} className="w-full flex justify-center" testId="clerk-auth-container">
          {mode === "in" ? (
            <SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp appearance={clerkAppearance} />
          ) : (
            <SignUp routing="path" path="/sign-up" signInUrl="/sign-in" appearance={clerkAppearance} />
          )}
        </Reveal>
        <Reveal delay={1.2} still={still}>
          <p className="text-sm text-zinc-500 max-w-sm text-center leading-relaxed px-4" data-testid="auth-invite-note">
            Access is by invitation. Brokers use the email address their invitation was sent to.
          </p>
        </Reveal>
      </main>
    </div>
  );
}
